import type {
  Deployment,
  LandingDetail,
  LandingMetrics,
  LandingSummary,
  Me,
  MetricsOverview,
  Release,
  ReleaseDetail,
} from "@/lib/types";

export class ApiError extends Error {
  status: number;
  detail: unknown;
  constructor(status: number, detail: unknown) {
    super(typeof detail === "string" ? detail : `HTTP ${status}`);
    this.status = status;
    this.detail = detail;
  }
}

/** True when the backend asked for a fresh prod step-up confirmation. */
export function isStepupRequired(e: unknown): boolean {
  return (
    e instanceof ApiError &&
    typeof e.detail === "object" &&
    e.detail !== null &&
    (e.detail as Record<string, unknown>).error === "stepup_required"
  );
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, { credentials: "same-origin", ...init });
  if (res.status === 204) return undefined as T;
  const body = res.headers.get("content-type")?.includes("application/json")
    ? await res.json()
    : await res.text();
  if (!res.ok) throw new ApiError(res.status, (body as { detail?: unknown })?.detail ?? body);
  return body as T;
}

export interface Health {
  status: string;
  auth_mode: "dev" | "oidc" | "facer";
  manage_nginx: boolean;
  facer_url: string | null;
}

export const api = {
  health: () => req<Health>("/api/health"),
  me: () => req<Me>("/api/auth/me"),
  devLogin: (name: string, roles: string[]) =>
    req<void>("/api/auth/dev-login", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ name, roles }),
    }),
  stepup: () => req<void>("/api/auth/stepup", { method: "POST" }),
  logout: () => req<void>("/api/auth/logout", { method: "POST" }),

  listLandings: () => req<LandingSummary[]>("/api/landings"),
  getLanding: (id: number) => req<LandingDetail>(`/api/landings/${id}`),
  getRelease: (id: number, rid: number) =>
    req<ReleaseDetail>(`/api/landings/${id}/releases/${rid}`),
  history: (id: number) => req<Deployment[]>(`/api/landings/${id}/history`),
  metricsOverview: (days: number) => req<MetricsOverview>(`/api/metrics?days=${days}`),
  landingMetrics: (id: number, days: number) =>
    req<LandingMetrics>(`/api/landings/${id}/metrics?days=${days}`),

  createLanding: (body: Record<string, unknown>) =>
    req<LandingDetail>("/api/landings", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    }),
  updateLanding: (id: number, body: Record<string, unknown>) =>
    req<LandingDetail>(`/api/landings/${id}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    }),
  deleteLanding: (id: number) =>
    req<void>(`/api/landings/${id}`, { method: "DELETE" }),

  testDeploy: (id: number, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return req<Release>(`/api/landings/${id}/test-deploy`, { method: "POST", body: form });
  },
  promote: (id: number, release_id: number, note?: string) =>
    req<Release>(`/api/landings/${id}/promote`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ release_id, note }),
    }),
  rollback: (id: number, release_id: number, note?: string) =>
    req<Release>(`/api/landings/${id}/rollback`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ release_id, note }),
    }),
  pause: (id: number) => req<void>(`/api/landings/${id}/pause`, { method: "POST" }),
  resume: (id: number) => req<void>(`/api/landings/${id}/resume`, { method: "POST" }),

  buildLogUrl: (id: number, rid: number) => `/api/landings/${id}/releases/${rid}/log`,
};
